import React, { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
  registerables as registerablesJS,
  InteractionItem,
} from 'chart.js';
import { Color, ChartDataSet } from '../../types/app/chart.types';
import { Chart, getElementsAtEvent } from 'react-chartjs-2';
import { ThemeType } from '../../hooks/useLocalTheme';
import { GRAY } from '../../utils/core/functions/colors.function';

ChartJS.register(...registerablesJS);
ChartJS.register(
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  LineElement,
  Legend,
  Tooltip,
);

interface Props {
  chartData: ChartDataSet[];
  labels: string[];
  displayLegend?: boolean;
  chartHeight?: string;
  chartWidth?: string;
  chartTitle?: string;
  onClick?: (result: InteractionItem[]) => void;
  localTheme?: ThemeType;
}

export function ChartsContainer({
  chartData,
  labels,
  displayLegend = true,
  chartHeight,
  chartWidth,
  chartTitle,
  onClick,
  localTheme,
}: Props) {
  const chartRef = useRef();

  const preparedData = {
    labels,
    datasets: chartData.map((dataset: ChartDataSet, index: number) => {
      return {
        type: dataset.chartType as any,
        label: dataset.label,
        backgroundColor: dataset.colors.map(
          (color: Color) => 'rgb(' + color.r + ',' + color.g + ',' + color.b + ')',
        ),
        borderColor: dataset.style.borderColor,
        borderWidth: dataset.style.borderWidth,
        fill: dataset.style.fill,
        showLine: dataset.style.showLine,
        pointStyle: dataset.style.pointStyle,
        borderDash: dataset.style.borderDash,
        pointRadius: dataset.style.pointRadius,
        order: index,
        hoverBorderWidth: dataset.style.borderWidth,
        data: dataset.y.map((item: any) => item),
      };
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: {
        grid: {
          color:
            localTheme && localTheme === ThemeType.DARK
              ? `rgb(${GRAY.r},${GRAY.g},${GRAY.b})`
              : undefined,
        },
      },

      xAxes: {
        grid: {
          display: false,
          color:
            localTheme && localTheme === ThemeType.DARK
              ? `rgb(${GRAY.r},${GRAY.g},${GRAY.b})`
              : undefined,
        },
      },
    },
    plugins: {
      legend: {
        display: displayLegend,
      },
      title: {
        display: true,
        text: chartTitle ? chartTitle : '',
      },
      tooltip: {
        filter: function (tooltipItems: any) {
          if (tooltipItems.dataset.label === 'Comments') {
            return false;
          } else {
            return true;
          }
        },
      },
    },
    animation: {
      duration: 0,
    },
    responsiveAnimationDuration: 0,
  };

  return (
    <>
      <div
        style={{
          height: chartHeight ? chartHeight : '400px',
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <Chart
          ref={chartRef}
          type="bar"
          data={preparedData}
          options={options}
          onClick={(event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
            if (!chartRef || !onClick) return;
            onClick(getElementsAtEvent(chartRef.current!, event));
          }}
        />
      </div>
    </>
  );
}
